import React from 'react';
import { Typography, Card, Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  ExperimentOutlined, 
  BuildOutlined, 
  PartitionOutlined,
  ApiOutlined,
  NodeIndexOutlined,
  ClusterOutlined,
  BranchesOutlined,
  FireOutlined
} from '@ant-design/icons';

const { Title, Paragraph } = Typography;

export default function StructureDesignPage() {
  const navigate = useNavigate();

  const designModules = [
    {
      key: 'esmfold',
      title: 'ESMFold',
      description: 'ESMFold蛋白质结构预测，支持多序列FASTA输入',
      icon: <ExperimentOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      features: [
        '多序列FASTA支持',
        '批量结构预测',
        '3D结构可视化'
      ],
      path: '/structure/esmfold',
      available: true
    },
    {
      key: 'protenix',
      title: 'Protenix',
      description: '基于ESM的蛋白质复合物结构预测（protenix_mini_esm_v0.5.0）',
      icon: <PartitionOutlined style={{ fontSize: '48px', color: '#fa8c16' }} />,
      features: [
        '多链复合物预测',
        '无需MSA',
        '快速预测模式',
        '3D结构可视化'
      ],
      path: '/structure/protenix',
      available: true
    },
    {
      key: 'bindcraft',
      title: 'BindCraft',
      description: '蛋白质结合位点设计工具（即将上线）',
      icon: <ApiOutlined style={{ fontSize: '48px', color: '#722ed1' }} />,
      features: [
        '结合位点设计',
        '亲和力优化',
        '特异性分析',
        '药物设计支持'
      ],
      path: '/structure/bindcraft',
      available: false
    },
    {
      key: 'rfdiffusion',
      title: 'RFdiffusion',
      description: '基于扩散模型的蛋白质从头设计工具（即将上线）',
      icon: <NodeIndexOutlined style={{ fontSize: '48px', color: '#13c2c2' }} />,
      features: [
        '从头设计',
        '扩散模型',
        '多样性生成',
        '功能导向设计'
      ],
      path: '/structure/rfdiffusion',
      available: false
    },
    {
      key: 'proteinmpnn',
      title: 'ProteinMPNN',
      description: '基于图神经网络的蛋白质序列设计工具',
      icon: <ClusterOutlined style={{ fontSize: '48px', color: '#f5222d' }} />,
      features: [
        '序列设计',
        '图神经网络',
        '结构约束',
        '稳定性优化'
      ],
      path: '/structure/proteinmpnn',
      available: true
    },
    {
      key: 'thermompnn',
      title: 'ThermoMPNN',
      description: '蛋白质稳定性预测工具，分析单点突变对稳定性的影响',
      icon: <FireOutlined style={{ fontSize: '48px', color: '#fa541c' }} />,
      features: [
        '稳定性预测',
        '突变分析',
        'ΔΔG计算',
        '位点扫描',
        'CPU兼容模式'
      ],
      path: '/structure/thermompnn',
      available: true
    },
    {
      key: 'hypermpnn',
      title: 'HyperMPNN',
      description: '热稳定蛋白质设计工具，基于超嗜热生物数据训练的序列设计模型',
      icon: <FireOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />,
      features: [
        '热稳定设计',
        '超嗜热优化',
        '精确位置控制',
        '保守性采样',
        '链特异设计'
      ],
      path: '/structure/hypermpnn',
      available: true
    },
    {
      key: 'antibody-denovo',
      title: 'Antibody De novo Design',
      description: '抗体从头设计平台（即将上线）',
      icon: <BranchesOutlined style={{ fontSize: '48px', color: '#eb2f96' }} />,
      features: [
        '抗体从头设计',
        'CDR区优化',
        '亲和力预测',
        '人源化设计'
      ],
      path: '/structure/antibody-denovo',
      available: false
    }
  ];

  const handleCardClick = (module: any) => {
    if (module.available) {
      navigate(module.path);
    }
  };

  return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Title level={2}>结构与设计</Title>
          <Paragraph style={{ fontSize: '16px', color: '#666' }}>
            提供蛋白质结构预测、从头设计、序列设计等多种功能
          </Paragraph>
        </div>

        <Row gutter={[24, 24]}>
          {designModules
            .filter((m) => m.key !== 'bindcraft' && m.key !== 'rfdiffusion')
            .map((module) => (
            <Col xs={24} md={6} key={module.key}>
              <Card
                hoverable={module.available}
                style={{ 
                  height: '100%',
                  cursor: module.available ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                  opacity: module.available ? 1 : 0.6
                }}
                bodyStyle={{ padding: '32px 24px' }}
                onClick={() => handleCardClick(module)}
              >
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  {module.icon}
                </div>
                
                <Title level={4} style={{ textAlign: 'center', marginBottom: 16 }}>
                  {module.title}
                  {!module.available && (
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#999', 
                      marginLeft: '8px',
                      fontWeight: 'normal'
                    }}>
                      (即将上线)
                    </span>
                  )}
                </Title>
                
                <Paragraph style={{ 
                  textAlign: 'center', 
                  color: '#666',
                  marginBottom: 24,
                  minHeight: '48px'
                }}>
                  {module.description}
                </Paragraph>
                
                <div>
                  <Title level={5} style={{ marginBottom: 12 }}>主要功能：</Title>
                  <ul style={{ paddingLeft: 20, margin: 0 }}>
                    {module.features.map((feature, index) => (
                      <li key={index} style={{ marginBottom: 4, color: '#666' }}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
  );
}